/**
 * Service Météo - Utilise Open-Meteo API (gratuit, sans clé API)
 * Récupère la météo actuelle et les prévisions basées sur la géolocalisation
 */

const WEATHER_CACHE_KEY = 'adhd_weather_cache';
const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes

// Codes météo Open-Meteo → descriptions et icônes
const WEATHER_CODES = {
  0: { desc: 'Ciel dégagé', icon: '☀️', category: 'clear' },
  1: { desc: 'Principalement clair', icon: '🌤️', category: 'clear' },
  2: { desc: 'Partiellement nuageux', icon: '⛅', category: 'cloudy' },
  3: { desc: 'Couvert', icon: '☁️', category: 'cloudy' },
  45: { desc: 'Brouillard', icon: '🌫️', category: 'fog' },
  48: { desc: 'Brouillard givrant', icon: '🌫️', category: 'fog' },
  51: { desc: 'Bruine légère', icon: '🌧️', category: 'rain' },
  53: { desc: 'Bruine modérée', icon: '🌧️', category: 'rain' },
  55: { desc: 'Bruine dense', icon: '🌧️', category: 'rain' },
  56: { desc: 'Bruine verglaçante légère', icon: '🌧️', category: 'rain' },
  57: { desc: 'Bruine verglaçante dense', icon: '🌧️', category: 'rain' },
  61: { desc: 'Pluie légère', icon: '🌧️', category: 'rain' },
  63: { desc: 'Pluie modérée', icon: '🌧️', category: 'rain' },
  65: { desc: 'Pluie forte', icon: '🌧️', category: 'rain' },
  66: { desc: 'Pluie verglaçante légère', icon: '🌧️', category: 'rain' },
  67: { desc: 'Pluie verglaçante forte', icon: '🌧️', category: 'rain' },
  71: { desc: 'Neige légère', icon: '🌨️', category: 'snow' },
  73: { desc: 'Neige modérée', icon: '🌨️', category: 'snow' },
  75: { desc: 'Neige forte', icon: '❄️', category: 'snow' },
  77: { desc: 'Grains de neige', icon: '🌨️', category: 'snow' },
  80: { desc: 'Averses légères', icon: '🌦️', category: 'rain' },
  81: { desc: 'Averses modérées', icon: '🌦️', category: 'rain' },
  82: { desc: 'Averses violentes', icon: '⛈️', category: 'storm' },
  85: { desc: 'Averses de neige légères', icon: '🌨️', category: 'snow' },
  86: { desc: 'Averses de neige fortes', icon: '❄️', category: 'snow' },
  95: { desc: 'Orage', icon: '⛈️', category: 'storm' },
  96: { desc: 'Orage avec grêle légère', icon: '⛈️', category: 'storm' },
  99: { desc: 'Orage avec grêle forte', icon: '⛈️', category: 'storm' }
};

// Conseils selon la météo
const WEATHER_TIPS = {
  clear: [
    "Parfait pour une balade !",
    "Belle journée pour sortir",
    "Profite du soleil !"
  ],
  cloudy: [
    "Journée calme en perspective",
    "Idéal pour se concentrer",
    "Temps doux aujourd'hui"
  ],
  rain: [
    "N'oublie pas ton parapluie !",
    "Journée cocooning idéale",
    "Préfère les activités intérieures"
  ],
  snow: [
    "Couvre-toi bien !",
    "Attention au verglas",
    "Parfait pour un chocolat chaud"
  ],
  fog: [
    "Visibilité réduite, prudence !",
    "Ambiance mystérieuse",
    "Roule doucement"
  ],
  storm: [
    "Reste à l'abri si possible",
    "Évite les déplacements",
    "Journée calme à la maison"
  ]
};

// Coordonnées par défaut (Paris 75019)
const DEFAULT_COORDS = {
  latitude: 48.8808,
  longitude: 2.3858
};

class WeatherService {
  constructor() {
    this.cache = null;
    this.loadCache();
  }

  loadCache() {
    try {
      const cached = localStorage.getItem(WEATHER_CACHE_KEY);
      if (cached) {
        const data = JSON.parse(cached);
        if (Date.now() - data.timestamp < CACHE_DURATION) {
          this.cache = data;
        }
      }
    } catch (e) {
      console.warn('Erreur chargement cache météo:', e);
    }
  }

  saveCache(data) {
    try {
      const cacheData = { ...data, timestamp: Date.now() };
      localStorage.setItem(WEATHER_CACHE_KEY, JSON.stringify(cacheData));
      this.cache = cacheData;
    } catch (e) {
      console.warn('Erreur sauvegarde cache météo:', e);
    }
  }

  async getLocation() {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve(DEFAULT_COORDS);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
        },
        () => {
          resolve(DEFAULT_COORDS);
        },
        { timeout: 5000, maximumAge: 600000 }
      );
    });
  }

  async fetchWeather(coords = null) {
    // Vérifier le cache
    if (this.cache && Date.now() - this.cache.timestamp < CACHE_DURATION) {
      return this.cache;
    }

    try {
      const location = coords || await this.getLocation();

      const response = await fetch(
        `https://api.open-meteo.com/v1/forecast?` +
        `latitude=${location.latitude}&longitude=${location.longitude}` +
        `&current=temperature_2m,apparent_temperature,weather_code,wind_speed_10m,relative_humidity_2m,surface_pressure,wind_direction_10m` +
        `&daily=temperature_2m_max,temperature_2m_min,weather_code,sunrise,sunset,uv_index_max,precipitation_sum` +
        `&timezone=Europe/Paris&forecast_days=1`
      );

      if (!response.ok) throw new Error('Erreur API météo');

      const data = await response.json();

      const weatherCode = data.current.weather_code;
      const weatherInfo = WEATHER_CODES[weatherCode] || WEATHER_CODES[0];

      const result = {
        current: {
          temperature: Math.round(data.current.temperature_2m),
          feelsLike: Math.round(data.current.apparent_temperature),
          humidity: data.current.relative_humidity_2m,
          windSpeed: Math.round(data.current.wind_speed_10m),
          windDirection: data.current.wind_direction_10m,
          windDirectionLabel: this.getWindDirection(data.current.wind_direction_10m),
          pressure: Math.round(data.current.surface_pressure),
          weatherCode: weatherCode,
          description: weatherInfo.desc,
          icon: weatherInfo.icon,
          category: weatherInfo.category
        },
        today: {
          tempMax: Math.round(data.daily.temperature_2m_max[0]),
          tempMin: Math.round(data.daily.temperature_2m_min[0]),
          sunrise: data.daily.sunrise[0].split('T')[1],
          sunset: data.daily.sunset[0].split('T')[1],
          uvIndex: Math.round(data.daily.uv_index_max[0]),
          precipitation: data.daily.precipitation_sum[0] ?? 0,
        },
        location: location,
        timestamp: Date.now()
      };

      this.saveCache(result);
      return result;

    } catch (error) {
      console.error('Erreur météo:', error);
      // Retourner le cache même expiré en cas d'erreur
      if (this.cache) return this.cache;

      // Données par défaut
      return {
        current: {
          temperature: null,
          description: 'Indisponible',
          icon: '❓',
          category: 'clear'
        },
        today: {},
        error: true
      };
    }
  }

  getTip(category) {
    const tips = WEATHER_TIPS[category] || WEATHER_TIPS.clear;
    return tips[Math.floor(Math.random() * tips.length)];
  }

  // Infos utiles du jour
  getDailyInfo(date = new Date()) {
    const dayOfWeek = date.getDay();
    const dayOfMonth = date.getDate();
    const month = date.getMonth();

    const infos = [];

    // Jour de la semaine spécial
    if (dayOfWeek === 1) infos.push({ icon: '💪', text: 'Lundi motivation !' });
    if (dayOfWeek === 5) infos.push({ icon: '🎉', text: 'Vendredi !' });
    if (dayOfWeek === 0 || dayOfWeek === 6) infos.push({ icon: '😴', text: 'Weekend' });

    // Début/fin de mois
    if (dayOfMonth <= 3) infos.push({ icon: '📅', text: 'Début de mois' });
    if (dayOfMonth >= 28) infos.push({ icon: '📅', text: 'Fin de mois' });

    // Heure de la journée
    const hour = new Date().getHours();
    if (hour < 7) infos.push({ icon: '🌙', text: 'Tôt le matin' });
    else if (hour < 12) infos.push({ icon: '☀️', text: 'Matinée' });
    else if (hour < 14) infos.push({ icon: '🍽️', text: 'Pause déjeuner' });
    else if (hour < 18) infos.push({ icon: '💼', text: 'Après-midi' });
    else if (hour < 21) infos.push({ icon: '🌆', text: 'Soirée' });
    else infos.push({ icon: '🌙', text: 'Bonne nuit' });

    // Rappels saisonniers
    if (month === 11 || month === 0 || month === 1) {
      infos.push({ icon: '❄️', text: 'Pense à te couvrir' });
    } else if (month >= 5 && month <= 7) {
      infos.push({ icon: '💧', text: 'Reste hydraté(e)' });
    }

    return infos;
  }

  // Convertit les degrés de direction du vent en point cardinal
  getWindDirection(degrees) {
    if (degrees == null) return '';
    const dirs = ['N', 'NE', 'E', 'SE', 'S', 'SO', 'O', 'NO'];
    return dirs[Math.round(degrees / 45) % 8];
  }

  // Indicateur "bon pour sortir" (true = vert, false = rouge, null = mitigé)
  getOutdoorScore(weatherData) {
    if (!weatherData?.current) return null;
    const { category, windSpeed } = weatherData.current;
    const { uvIndex = 0 } = weatherData.today || {};
    if (['storm', 'snow'].includes(category)) return false;
    if (category === 'rain' || uvIndex > 8 || windSpeed > 50) return null;
    if (['clear', 'cloudy', 'fog'].includes(category) && uvIndex <= 6 && windSpeed <= 30) return true;
    return null;
  }

  // Phrase motivante du jour
  getDailyMotivation() {
    const motivations = [
      "Tu vas gérer cette journée !",
      "Chaque petit pas compte",
      "Respire, tu es capable",
      "Une tâche à la fois",
      "Fais de ton mieux, c'est suffisant",
      "Tu as déjà fait tant de choses difficiles",
      "Aujourd'hui est un nouveau départ",
      "Sois fier(e) de toi",
      "Prends soin de toi en premier",
      "Le progrès, pas la perfection"
    ];

    // Utiliser la date comme seed pour avoir la même phrase toute la journée
    const today = new Date();
    const seed = today.getFullYear() * 10000 + (today.getMonth() + 1) * 100 + today.getDate();
    const index = seed % motivations.length;

    return motivations[index];
  }
}

const weatherService = new WeatherService();
export default weatherService;
